import { gsap } from 'gsap';
import { Flip } from 'gsap/Flip';
import { carousel } from './carousel';
import { scrollNavigation } from './scrollNavigation';
import { mq } from '../util/matchMedia';
import Swiper from 'swiper';
import SwiperCore, { Navigation } from 'swiper/core';
import { readGlobalState, setGlobalState } from '../util/helpers';

SwiperCore.use([Navigation]);
gsap.registerPlugin(Flip);

export const lightbox = (() => {
  let swiper, gallery, lightbox, closeButton, flipMedia;

  // Current state
  const current = {};

  const init = () => {
    mq.init(); // Check current breakpoint
    getReferenceElements();
    initSwiper();
    removeDuplicateFlipIds();
    closeButton.addEventListener('click', close);
  }

  const getReferenceElements = () => {
    gallery = document.querySelector(`.js-gallery-${readGlobalState('mq')}`);
    lightbox = document.querySelector('.js-lightbox');
    closeButton = lightbox.querySelector('.js-close-lightbox');
  }

  const initSwiper = () => {
    // eslint-disable-next-line
    swiper = new Swiper('.swiper-container', {
      speed: mq.isDesktop ? 1000 : 500,
      loop: true,
      navigation: {
        nextEl: '.swiper-button-next',
        prevEl: '.swiper-button-prev',
      },
    });

    swiper.on('slideChange', function () {
      current.mediaIndex = this.realIndex; // Ignores duplicates
    });

    swiper.on('slideChangeTransitionEnd', () => {
      createReverseFlipAnimation();
    });
  }

  // Make sure each media element only has one 
  // corresponding full-size version to FLIP to
  const removeDuplicateFlipIds = () => {
    const duplicates = lightbox.querySelectorAll('.swiper-slide-duplicate');
    duplicates.forEach(item => {
      const media = item.querySelector('.js-lightbox-media-container');
      media.removeAttribute('data-flip-id');
    });
  }

  const loadCollection = (index) => {
    // Load current (or specified) collection into lightbox
    current.collectionIndex = index === undefined ? current.collectionIndex : index;

    current.collection = gallery.querySelector(`
      .js-gallery-collection[data-index="${current.collectionIndex}"]
    `);

    const collection = getCurrentCollection();
    if (!swiper.slides) return;

    // swiper.slides includes the duplicate slides that Swiper creates
    swiper.slides.forEach((slide) => {
      const media = slide.querySelector('.js-lightbox-media-container');
      const mediaIndex = media.dataset.slideIndex;

      media.replaceChildren(createMediaElement(collection[mediaIndex], mediaIndex));
    });
  }

  const lightboxTransitioning = () => readGlobalState('lightboxTransitioning');

  const setLightboxTransitionState = (bool) => {
    setGlobalState('lightboxTransitioning', bool);
  }

  const open = (target) => {
    // Don't interrupt current transition
    if (lightboxTransitioning()) return;
    setLightboxTransitionState(true);

    carousel.pause(); // Pause looping galleries
    swiper.activeIndex = parseInt(target.dataset.mediaIndex, 10);
    current.collectionIndex = parseInt(target.dataset.collectionIndex, 10);
    current.mediaIndex = swiper.activeIndex;
    goToActiveSlide(0);
    animateCurrentMediaItem();

    if (mq.isDesktop) {
      scrollNavigation.removeTouchEventListeners();
      scrollNavigation.removeWheelEventListener();
    }

    setGlobalState('lightboxOpen', true);
  }

  const getCurrentElementState = () => {
    const thumbnail = gallery.querySelector(`[data-flip-id="media-${current.mediaIndex}"]`);
    const fullSize = lightbox.querySelector(`[data-flip-id="media-${current.mediaIndex}"]`);

    current.mediaState = Flip.getState([thumbnail, fullSize]);
  }

  const createFlipAnimation = () => {
    getCurrentElementState();

    flipMedia = Flip.from(current.mediaState, {
      duration: 1,
      zIndex: 3,
      ease: 'kota.easeInOut.8',
    });
  }

  const createReverseFlipAnimation = () => {
    const thumbnail = gallery.querySelector(`[data-flip-id="media-${current.mediaIndex}"]`);
    const currentSlide = lightbox.querySelector('.swiper-slide-active .full-size');

    currentSlide.dataset.flipId = `media-${current.mediaIndex}`;
    current.mediaState = Flip.getState([thumbnail, currentSlide]);
  }

  const animateCurrentMediaItem = () => {
    const tl = gsap.timeline({
      onComplete: () => setLightboxTransitionState(false),
    });

    gsap.set(lightbox, {
      autoAlpha: 1,
      backgroundColor: 'transparent',
    });

    createFlipAnimation();

    tl.add(flipMedia)

      .to(lightbox, {
        backgroundColor: 'white',
        duration: 0.2,
        overwrite: true,
        onComplete: () => {
          document.body.classList.add('lightbox-open');
        },
      }, 0)

      .to('.js-lightbox-button', {
        autoAlpha: 1,
        duration: 0.4,
        overwrite: true,
      }, '<')

      .set(lightbox, { pointerEvents: 'auto' }, 0);
  }

  const close = () => {
    // Don't interrupt current transition
    if (lightboxTransitioning()) return;
    setLightboxTransitionState(true);

    const posters = document.querySelectorAll('.js-gallery-media-poster');
    gsap.set(posters, { autoAlpha: 1 })

    const tl = gsap.timeline({
      onComplete: () => {
        gsap.set('.full-size', { clearProps: true });
        carousel.resume();
        document.body.classList.remove('lightbox-open');
        removeDuplicateFlipIds();
        setLightboxTransitionState(false);

        if (mq.isDesktop) {
          scrollNavigation.addTouchEventListeners();
          scrollNavigation.addWheelEventListener();
        }
      },
    });

    tl
      .set(lightbox, { pointerEvents: 'none' })

      .to('lightbox', {
        background: 'transparent',
        duration: 1,
      })

      .to('.js-lightbox-button', {
        autoAlpha: 0,
        duration: 0.2,
        overwrite: true,
      }, 0)

      .add(Flip.to(current.mediaState, {
        duration: 1,
        zIndex: 3,
        ease: 'kota.easeInOut.8',
      }), '<')
    
      .to(lightbox, {
        duration: 1,
        autoAlpha: 0,
      }, '<0.8')
    
      .set('.full-size', {
        autoAlpha: 0,
      });
    
    setGlobalState('lightboxOpen', false);
  }

  const createVideoElement = (data, i) => {
    const video = document.createElement('video');
    const source = document.createElement('source');
    const fallback = document.createElement('p');

    video.muted = true;
    video.setAttribute('playsinline', true);
    video.loop = true;
    video.autoplay = true;
    video.width = data.width * 2;
    video.height = data.height * 2;
    video.dataset.flip = `media${i}`;

    fallback.innerText = 'Sorry, your browser does not support embedded videos';

    source.src = data.src;
    source.type = data.mime_type;

    video.append(source, fallback);
    return video;
  }

  const createImageElement = (data, i) => {
    const img = document.createElement('img');
    img.dataset.flip = `media${i}`;
    img.alt = data.alt;
    img.width = data.width * 2;
    img.height = data.height * 2;
    img.src = data.src;
    img.srcset = data.srcset;
    img.sizes = 'auto';
    return img;
  }

  const createMediaElement = (data, i) => {
    return data.type === 'video'
      ? createVideoElement(data, i)
      : createImageElement(data, i);
  }

  const goToActiveSlide = (duration = 1) => {
    swiper.slideTo(swiper.activeIndex + 1, duration);
  }

  const getCurrentCollection = () => {
    const allCollections = readGlobalState('collections') || {};
    return allCollections[readGlobalState('mq')][current.collectionIndex];
  }

  const destroy = () => {
    swiper = {};
    closeButton.removeEventListener('click', close);
    setGlobalState('lightboxOpen', false);
  }

  return { init, open, destroy, loadCollection }

})();